/**
 * rebuy-gift-sync.js
 *
 * Auto-adds / removes Rebuy "Free Gift with Purchase" products in the theme's
 * custom ajax cart, using the Rebuy data source (rules engine) only. Gifts are
 * delivered into our own cart instead of relying on Rebuy Smart Cart UI, and are
 * zeroed out by the cwvessel bundle-discount Shopify Function (any line carrying
 * the hidden "_rebuy_gift" property gets 100% off).
 *
 * Concurrency: cart operations are serialized by a UI lock in z__jsAjaxCart.js
 * (one operation at a time — see _setCartLock / _cartBusy). So this stays simple:
 * whenever the cart re-renders, reconcile gifts, render ONCE, then release the lock.
 *
 * Safety: if the Rebuy call fails we leave existing gifts untouched (fetchDesired
 * returns null). The add/remove diff is idempotent (never adds a gift already present).
 *
 * Config below mirrors the "Free Gift with Purchase" widget (data source 299535).
 */
(function () {
  var GIFT_PROP = '_rebuy_gift';
  var ENDPOINT = 'https://rebuyengine.com/api/v1/custom/id/299535';
  var KEY = 'ea09c6668fe214c949b9847b69d4e3e08fc91a9c';
  var GIFT_LIMIT = 10;

  var RebuyGift = {
    busy: false,
    pending: false,
    desiredCache: {}, // cart signature -> [{productId, variantId}]

    isGift: function (item) {
      return !!(item.properties && item.properties[GIFT_PROP]);
    },

    getCart: function () {
      return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
        .then(function (r) { return r.json(); });
    },

    // Resolves to an array of {productId, variantId} on success (possibly empty),
    // or null on failure so the caller knows NOT to touch existing gifts.
    fetchDesired: function (productIds, subtotalCents) {
      if (!productIds.length) { return Promise.resolve([]); }
      // Pass the REAL paid subtotal (cents) so Rebuy can evaluate the
      // "cart_subtotal > $99" gift rules accurately. Without it Rebuy only sums
      // the distinct product ids we send (no quantities) and under-counts
      // high-quantity carts, which would drop the gift. (Rebuy expects cents.)
      var url = ENDPOINT + '?key=' + KEY +
        '&shopify_product_ids=' + productIds.join(',') +
        '&cart_subtotal=' + (subtotalCents || 0) +
        '&limit=' + GIFT_LIMIT + '&filter_inputs=no';
      return fetch(url)
        .then(function (r) { if (!r.ok) { throw new Error('rebuy http ' + r.status); } return r.json(); })
        .then(function (res) {
          if (!res || !res.data) { return []; }
          return res.data.map(function (p) {
            var v = p.variants && p.variants[0];
            return v ? { productId: p.id, variantId: v.id } : null;
          }).filter(Boolean);
        })
        .catch(function () { return null; });
    },

    addGift: function (variantId) {
      var props = {};
      props[GIFT_PROP] = '1';
      return fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 1, properties: props })
      });
    },

    removeLine: function (key) {
      return fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: 0 })
      });
    },

    // Coalescing: if called while running, re-run afterwards so gifts settle on
    // the final cart state. Resolves true if the cart was changed.
    sync: function () {
      var self = this;
      if (self.busy) { self.pending = true; return Promise.resolve(false); }
      self.busy = true;
      self.pending = false;
      return self._reconcile().then(function (changed) {
        self.busy = false;
        if (self.pending) {
          self.pending = false;
          return self.sync().then(function (more) { return changed || more; });
        }
        return changed;
      }, function () {
        self.busy = false;
        return false;
      });
    },

    _reconcile: function () {
      var self = this;
      return self.getCart().then(function (cart) {
        var gifts = cart.items.filter(function (i) { return self.isGift(i); });
        var nonGift = cart.items.filter(function (i) { return !self.isGift(i); });
        var productIds = nonGift.map(function (i) { return i.product_id; })
          .filter(function (v, idx, arr) { return arr.indexOf(v) === idx; });
        // Sum the paid (non-gift) line prices — in cents — so the cart_subtotal
        // sent to Rebuy matches the real cart (handles quantities). Include it in
        // the cache signature so crossing the $99 threshold re-queries.
        var subtotalCents = nonGift.reduce(function (s, i) { return s + (i.final_line_price || 0); }, 0);
        var sig = productIds.slice().sort().join(',') + '|' + subtotalCents;

        var desiredPromise = self.desiredCache.hasOwnProperty(sig)
          ? Promise.resolve(self.desiredCache[sig])
          : self.fetchDesired(productIds, subtotalCents).then(function (d) {
              if (d !== null) { self.desiredCache[sig] = d; }
              return d;
            });

        return desiredPromise.then(function (desired) {
          if (desired === null) { return false; } // Rebuy errored — don't touch gifts

          var desiredProductIds = desired.map(function (d) { return d.productId; });
          var currentGiftProductIds = gifts.map(function (i) { return i.product_id; });
          var toAdd = desired.filter(function (d) { return currentGiftProductIds.indexOf(d.productId) === -1; });
          var toRemove = gifts.filter(function (i) { return desiredProductIds.indexOf(i.product_id) === -1; });

          if (!toAdd.length && !toRemove.length) { return false; }

          // Sequential — runs inside the cart lock, so no other writes interleave.
          var chain = Promise.resolve();
          toRemove.forEach(function (item) { chain = chain.then(function () { return self.removeLine(item.key); }); });
          toAdd.forEach(function (d) { chain = chain.then(function () { return self.addGift(d.variantId); }); });
          return chain.then(function () { return true; });
        });
      });
    }
  };

  window.PXUTheme = window.PXUTheme || {};
  window.PXUTheme.rebuyGift = RebuyGift;

  // Clear the loading spinner + release any cart lock set by z__jsAjaxCart.js.
  function finish(jac) {
    if (typeof jac._setCartLoading === 'function') { jac._setCartLoading(false); }
    jac._cartBusy = false;
    if (typeof jac._setCartLock === 'function') { jac._setCartLock(false); }
  }

  // Hook the ajax cart's re-render: show loading, reconcile gifts, render ONCE,
  // then clear loading + unlock once the render has actually painted.
  function hook() {
    var jac = window.PXUTheme && window.PXUTheme.jsAjaxCart;
    if (!jac || typeof jac.updateView !== 'function' || jac.__rebuyGiftHooked) { return false; }

    var orig = jac.updateView.bind(jac);
    jac.updateView = function () {
      if (typeof jac._setCartLoading === 'function') { jac._setCartLoading(true); }
      RebuyGift.sync().then(render, render);
      function render() {
        var rendered;
        try { rendered = orig(); } catch (e) { rendered = null; }
        // orig() returns the asyncView deferred — wait for the DOM to paint, then finish.
        Promise.resolve(rendered).then(function () { finish(jac); }, function () { finish(jac); });
      }
    };
    jac.__rebuyGiftHooked = true;

    RebuyGift.sync().then(function (changed) { if (changed) { orig(); } }); // reconcile once on load
    return true;
  }

  if (!hook()) {
    var tries = 0;
    var timer = setInterval(function () { if (hook() || ++tries > 50) { clearInterval(timer); } }, 200);
  }
})();
